import InteractionsChip from "./InteractionsChip";
import { calculateDdiColor } from "./utils";
import { useTranslation } from "react-i18next";
import React, { useContext, useEffect, useState } from "react";
import RiskInfo from "./RiskInfo";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import { Consts } from "../pages/consts";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {faTrash, faXmark} from "@fortawesome/free-solid-svg-icons";
import {formatDateTime} from "../pages/utils";

const DrugsAccordionHeaderContent = ({ drug, onDeleteDrug, patDrugs, toggleControl }) => {
  const { t } = useTranslation();
  const [showRisk, setShowRisk] = useState(false);
  const [dosageReport, setDosageReport] = useState(null);
  const [stopDialogOpened, setStopDialogOpened] = useState(false);
  const [stopDate, setStopDate] = useState("");
  const context = useContext(InitContext);
  const {clearError, getDosageRecommendationReport, getDosageRecommendationReportFile} = useMmcService();

  useEffect(() => {
    clearError();

    // get report only for active drugs
    if(drug?.status === "active"){
      getDosageRecommendationReport(context?.token, drug?.id).then((report) => {
        const isComplexDrug = drug?.activeSubstance?.includes("&&&"); // has more than one substance

        if(report?.message === "no model found for this medicine" && !isComplexDrug){
          setDosageReport({...report, message2: t("no_model_found")}); //
        } else  if(report?.message === "This medicine is not included in the ABDA database" && !isComplexDrug){
          setDosageReport({...report, message2: report?.message});
        } else if(report?.message.includes("pdm_not_sent") && !isComplexDrug){
          setDosageReport({...report, message2: t(report?.message)});
        }  else if((report?.message.includes("precheck request failed")
            || report?.message.includes("no victim for adjustment found in compounds")
            || report?.message.includes("patient's year of birth is missing in Access Control"))
          && !isComplexDrug){
          setDosageReport({...report, message2: report?.message});
        } else if(!dosageReport?.id && drug?.status === "active" && patDrugs?.length > 1 && !isComplexDrug){
          setDosageReport({...report, message2: t("update")});
        } else {
          setDosageReport(report?.message)
        }
      });
    } else {
      setDosageReport({message2: '-'})
    }
  }, []);

  let relativeRisk = +drug?.ddgi?.relative_risk_change;
  let riskChangeText;

  if (!drug?.ddgi) {
    riskChangeText = Consts.NOT_AVAILABLE;
  } else if (relativeRisk === -1) {
    riskChangeText = "see_explanation";
  } else if(relativeRisk === 0){
    riskChangeText = "is_not_increased";
  } else {
    relativeRisk = (Math.round(relativeRisk * 100 * 100) / 100).toFixed(2);

    const decrOrIncr = relativeRisk < 0
      ? t("has_decreased_by")
      : t("has_increased_by");

    riskChangeText =  decrOrIncr + " " + Math.abs(relativeRisk) + "%";
  }

  const isStandardRiskChangeText =  riskChangeText === "see_explanation"
    || riskChangeText === "is_not_increased" || riskChangeText === Consts.NOT_AVAILABLE
    || riskChangeText === "not_available_for_single_drug";

  const handleReportDownload = async (prescriptionId) => {
    const result = await getDosageRecommendationReportFile(context?.token, prescriptionId);

    if (result) {
      const { blob, filename } = result;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body?.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const sortedDdis = drug?.ddis?.sort((a, b) => {
    if (a.ddiDrug?.activeSubstance < b.ddiDrug?.activeSubstance) {
      return -1;
    }

    if (a.ddiDrug?.activeSubstance > b.ddiDrug?.activeSubstance) {
      return 1;
    }

    return 0;
  });

  const substance = drug?.activeSubstance === Consts.ANY
    ? t(Consts.NOT_SPECIFIED)?.toLowerCase()
    : drug?.activeSubstance;

  const handleStopDate = () => {
    setStopDialogOpened(false);
    onDeleteDrug(drug, stopDate);
  }

  return (
    <div
      className="w3-row w3-padding-small accordion-header-row drugs-header-content-row"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="drugs-header-leading-controls">
        {((context?.role === Consts.PATIENT || context?.role === Consts.HCP_PRESCRIBER) && toggleControl && drug?.status === "active") &&
          <div className="drugs-stop-action">
            <button
              type="button"
              className="symptom-ui-button symptom-ui-button--icon symptom-ui-button--danger"
              onClick={() => setStopDialogOpened(true)}
              aria-label={t("stop_drug")}
              title={t("stop_drug")}
            >
              <FontAwesomeIcon icon={faTrash} />
            </button>
          </div>
        }
      </div>

      {toggleControl && <span className="drugs-header-toggle-inline">{toggleControl}</span>}

      {/*<div className={context?.role === Consts.PATIENT
        ? "w3-col s5 m5 w3-mobile drugs-header-main-col patient-drugs-main-col"
        : "w3-col s2 m2 w3-mobile drugs-header-main-col"}
      >*/}
      <div className="w3-col s2 m2 w3-mobile drugs-header-main-col">
        <label className="bold-label w3-block">
          {drug?.productName === Consts.NOT_SPECIFIED || drug?.productName === Consts.ANY
            ? t(Consts.NOT_SPECIFIED)?.toLowerCase()
            : drug?.productName}
        </label>
        {drug?.activeSubstance && (
          <>
            <label className="w3-block">{`${t("active_substance")}: `}</label>
            <label>{`${substance?.replaceAll("&&&", ", ")}`}</label>
            <label>{`, atc: ${drug?.atc ? drug?.atc : "-"}`}</label>
          </>
        )}
        <label className="w3-block">
          {`${t("start_date")}: ${drug?.startDate}`}
        </label>
        {drug?.status !== "active" && <label className="w3-block" style={{color: "red"}}>
          {`${t("discontinued")}: ${formatDateTime(drug?.status, true)}`}
        </label>}
      </div>
      { stopDialogOpened && (
        <div className="w3-modal feedback-modal">
          <div className="w3-modal-content feedback-modal-content w3-center">
            <header className="feedback-modal-header">
              <h2>{t("stop_drug_date")}</h2>
              <FontAwesomeIcon
                icon={faXmark}
                className="modal-close"
                onClick={() => setStopDialogOpened(false)}
              />
            </header>
            <div className="w3-row w3-padding-small w3-margin w3-center">
              <input
                type="date"
                value={stopDate}
                className="w3-input w3-border w3-round form-input w3-col"
                onChange={(e) => setStopDate(e.target.value)}
              />
              <button onClick={handleStopDate} className="w3-btn w3-round-xlarge w3-theme-d5 ">
                {t("save")}
              </button>
            </div>
          </div>
        </div>
      ) }
      {/*<div className={ context?.role === Consts.PATIENT
        ? "w3-col s5 m5 w3-mobile interactions-col patient-drugs-interactions-col"
        : "w3-col s4 m4 w3-mobile interactions-col" }
      > */}
      <div className="w3-col s4 m4 w3-mobile interactions-col">
        <div className="w3-container">
          <div className="w3-padding-small interactions-panel drugs-header-panel">
            <label className="bold-label interactions-title-label">{t("interactions_between")}</label>
            <div className="w3-row interactions-chip-row">
              <InteractionsChip
                label={t("ddi")}
                title={t("drug_drug_interactions")}
                color={ patDrugs?.length === 1 ? Consts.GREY : calculateDdiColor(sortedDdis, context?.role) }
                drug={drug}
              />
              <InteractionsChip
                label={t("dgi")}
                title={t("drug_gene_interactions")}
                color={drug?.dgis?.length > 0 ? "#1565a0" : Consts.GREY}
                drug={drug}
              />
            </div>
          </div>
        </div>
      </div>
      {/*{context?.role !== Consts.PATIENT && (*/}
        <>
          <div className="w3-col s3 m3 w3-mobile">
            <div>
              <div className="w3-padding-small drugs-header-panel">
                <label className="bold-label">{t("adverse_event_risks")}</label>
                {drug?.status === "active" ? <label
                  className={`${drug?.ddgi ? "underlined-text" : "not_underlined-text"} drugs-risk-change-label label-link`}
                  onClick={drug?.ddgi ? () => setShowRisk(true) : undefined}
                >
                  {isStandardRiskChangeText ? t(riskChangeText) : riskChangeText}
                </label> : '-'}
                <div className="w3-half w3-padding-small">
                  {drug?.status === "active" && <RiskInfo
                    showRisk={showRisk}
                    setShowRisk={setShowRisk}
                    drug={drug}
                  />}
                </div>
              </div>
            </div>
          </div>
          <div className="w3-col s3 m3 w3-mobile drugs-dosage-col">
            <div >
              <div className="w3-padding-small drugs-header-panel">
                <label className="bold-label">{t("dosage_recommendation")}</label>
                {(drug?.status === "active" && dosageReport?.reportAvailable) && (
                  <label
                    className="label-link"
                    style={{
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                      display: 'block',
                      maxWidth: '100%'
                    }}
                    onClick={() =>
                      handleReportDownload(dosageReport?.prescriptionId)
                    }
                  >
                    {t("report_for")} {drug?.productName} {new Date(dosageReport?.date).toLocaleDateString()}
                  </label>
                )}
                {(!dosageReport?.reportAvailable && dosageReport?.orderId && drug?.status === "active" && dosageReport?.message) && (
                  <div><label>{t("requested")}</label></div>
                )}
                {(!dosageReport?.reportAvailable && !dosageReport?.orderId && drug?.status === "active" && dosageReport?.message) && (
                    <label>{dosageReport?.message2}</label>
                )}
                {(!dosageReport?.reportAvailable && !dosageReport?.message && drug?.status === "active" && dosageReport?.id) &&
                  <label>{t("requested")}</label>
                }
                {(drug.activeSubstance?.includes("&&&") && drug?.status === "active") && <label>{t("not_requested")}</label>}
                {(!dosageReport?.id && drug?.status === "active" && patDrugs?.length > 1) && <label>{dosageReport?.message2}</label>}
                {(!dosageReport?.id && drug?.status === "active" && patDrugs?.length === 1) && <label>{t("not_available_for_single_drug")}</label>}
                {drug?.status !== "active" && <label>{dosageReport?.message2}</label>}
              </div>
            </div>
          </div>
        </>
      {/*)}*/}
    </div>
  );
};

export default DrugsAccordionHeaderContent;
